import { RotateCcw, Volume2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useVoice } from "../../contexts/VoiceContext";
import type { VoiceSettings } from "../../services/VoiceService";
import { Button } from "./button";
import { Card } from "./card";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Slider } from "./slider";

interface VoiceSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function VoiceSettingsModal({ isOpen, onClose }: VoiceSettingsModalProps) {
    const { state, updateSettings } = useVoice();
    const [localSettings, setLocalSettings] = useState<VoiceSettings>(state.settings);

    // Update local settings when modal opens or state changes
    useEffect(() => {
        if (isOpen) {
            setLocalSettings(state.settings);
        }
    }, [isOpen, state.settings]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    // Handle backdrop click
    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const handleSettingChange = (
        key: keyof VoiceSettings,
        value: number | boolean | SpeechSynthesisVoice | undefined
    ) => {
        const newSettings = { ...localSettings, [key]: value };
        setLocalSettings(newSettings);
        updateSettings({ [key]: value });
    };

    const handleReset = () => {
        const defaultSettings: VoiceSettings = {
            rate: 1,
            pitch: 1,
            volume: 0.8,
            autoScroll: true,
        };
        setLocalSettings(defaultSettings);
        updateSettings(defaultSettings);
    };

    const handleVoiceChange = (voiceName: string) => {
        if (voiceName === "default") {
            handleSettingChange("voice", undefined);
        } else {
            const selectedVoice = state.availableVoices.find((voice: SpeechSynthesisVoice) => voice.name === voiceName);
            if (selectedVoice) {
                handleSettingChange("voice", selectedVoice);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/20 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={handleBackdropClick}
        >
            <Card
                className="max-w-md w-full p-6 space-y-6 shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
                style={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))" }}
            >
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Volume2 className="h-5 w-5" />
                        <h3 className="text-lg font-semibold">Voice Settings</h3>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleReset}
                        title="Reset to defaults"
                        className="h-8 w-8"
                    >
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                </div>

                {/* Settings */}
                <div className="space-y-6">
                    {/* Speed */}
                    <Slider
                        label="Speed"
                        value={localSettings.rate}
                        onChange={(value) => handleSettingChange("rate", value)}
                        min={0.1}
                        max={3}
                        step={0.1}
                    />

                    {/* Pitch */}
                    <Slider
                        label="Pitch"
                        value={localSettings.pitch}
                        onChange={(value) => handleSettingChange("pitch", value)}
                        min={0}
                        max={2}
                        step={0.1}
                    />

                    {/* Volume */}
                    <Slider
                        label="Volume"
                        value={localSettings.volume}
                        onChange={(value) => handleSettingChange("volume", value)}
                        min={0}
                        max={1}
                        step={0.1}
                    />

                    {/* Voice Selection */}
                    {state.availableVoices.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Voice</label>
                            <Select
                                value={localSettings.voice?.name || localSettings.voiceName || "default"}
                                onValueChange={handleVoiceChange}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Default Voice" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Default Voice</SelectItem>
                                    {state.availableVoices.map((voice: SpeechSynthesisVoice) => (
                                        <SelectItem key={voice.name} value={voice.name}>
                                            {voice.name} ({voice.lang})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Auto-continue Setting */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">Auto-scroll</label>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="auto-scroll"
                                checked={localSettings.autoScroll !== false}
                                onCheckedChange={(checked: boolean | "indeterminate") =>
                                    handleSettingChange("autoScroll", checked === true)
                                }
                            />
                            <label htmlFor="auto-scroll" className="text-sm text-muted-foreground cursor-pointer">
                                Automatically scroll to next section and follow along
                            </label>
                        </div>
                    </div>

                    {/* Preview Button */}
                    <Button
                        variant="outline"
                        onClick={() => {
                            // Stop any current speech before testing
                            window.speechSynthesis.cancel();

                            // Test the current settings
                            const testText = "This is how your voice settings sound.";
                            // Create a temporary utterance to test without affecting global state
                            const testUtterance = new SpeechSynthesisUtterance(testText);
                            testUtterance.rate = localSettings.rate;
                            testUtterance.pitch = localSettings.pitch;
                            testUtterance.volume = localSettings.volume;
                            if (localSettings.voice) {
                                testUtterance.voice = localSettings.voice;
                            }
                            window.speechSynthesis.speak(testUtterance);
                        }}
                        className="w-full"
                        disabled={!state.isSupported}
                    >
                        Test Voice Settings
                    </Button>
                </div>

                {/* Footer */}
                <div className="flex gap-2 justify-end pt-4 border-t">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </Card>
        </div>
    );
}
